/* eslint-disable operator-linebreak */
import React, { useCallback, useMemo, useState } from 'react';
import cc from 'classnames';
import * as yup from 'yup';
import { useMediaQuery, useTheme, CircularProgress } from '@material-ui/core';
import arrayMutators from 'final-form-arrays';
import { FormApi } from 'final-form';
import { changeFieldValue, setFieldTouched } from 'utils/formMutators';
import Form from 'ui/Form/Form';
import DashboardCard from 'ui/DashboardCard/DashboardCard';
import Button from 'ui/Button/Button';
import { Select, SelectItem } from 'ui/Input';
import NavigationTitle, { LayoutType } from 'ui/NavigationTitle/NavigationTitle';
import NavigateBack from 'ui/NavigateBack/NavigateBack';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import api from 'api/api';
import { StatusModal } from 'components/modals';
import { AxiosStatus } from 'api/axiosConfig';
import { requiredOnly, validateAccount, validationCallbackOld } from 'utils/validation';
import { IApiResponse } from 'utils/models';
import { IBank, IFormValues, IAdditionalData, Category } from './Beneficiaries.model';
import AddBeneficiaryLocal from './AddBeneficiary/AddBeneficiaryLocal';
import AddBeneficiaryOtherBank from './AddBeneficiary/AddBeneficiaryOtherBank';
import AddBeneficiaryMobileMoney from './AddBeneficiary/AddBeneficiaryMobileMoney';
import AddBeneficiaryPrepaidCard from './AddBeneficiary/AddBeneficiaryPrepaidCard';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './Beneficiaries.module.css';

type AddBenFormType = FormApi<IFormValues, Partial<IFormValues>>;

const schema: Record<Category, yup.AnyObjectSchema> = {
  FABTransfer: yup.object().shape({
    fullName: requiredOnly('Full name'),
    accountNumber: validateAccount(),
  }),
  otherBankTransfer: yup.object().shape({
    fullName: requiredOnly('Full name'),
    instantType: requiredOnly('Transfer Type'),
    bankCode: requiredOnly('Bank'),
    accountNumber: validateAccount(),
  }),
  otherBankTransferNonInstant: yup.object().shape({
    fullName: requiredOnly('Full name'),
    instantType: requiredOnly('Transfer Type'),
    bankCode: requiredOnly('Bank'),
    // bankBranchCode: requiredOnly('Bank Branch'),
    accountNumber: validateAccount(),
  }),
  mobilemoney: yup.object().shape({
    beneficiaryName: requiredOnly('Full name'),
    networkCode: requiredOnly('Network'),
    beneficiaryWalletNumber: requiredOnly('Wallet Number'),
  }),
  prepaidcard: yup.object().shape({
    name: requiredOnly('Full name'),
    cardCustomerId: requiredOnly('Customer ID'),
    cardName: requiredOnly('Card name'),
    phoneNumber: requiredOnly('Phone number'),
    last4digit: requiredOnly('Card’s last 4 digits').min(4, '4 digits required'),
  }),
};

const categories = {
  FABTransfer: 'Transfers to First Atlantic Bank PLC',
  otherBankTransfer: 'Transfers to Other Bank',
  mobilemoney: 'Mobile Money',
  prepaidcard: 'Prepaid Card',
};

enum States {
  selectingCategory = 'selectingCategory',
  fillingInfo = 'fillingInfo',
}

type Props = {
  onClose: () => void;
  additionalData: IAdditionalData;
  initCategory: Category;
};

const beneficiaryTypes = {
  FABTransfer: 'transfer/add',
  otherBankTransfer: 'transfer/add',
  mobilemoney: 'mobilemoney/add',
  prepaidcard: 'prepaidcard/add',
};

const AddBeneficiaryForm = ({ onClose, additionalData, initCategory }: Props) => {
  const [state, setState] = useState<States>(States.selectingCategory);
  const openCategorySelector = () => setState(States.selectingCategory);

  const [nameHint, setNameHint] = useState('');
  const [currency, setCurrency] = useState('');
  const [beneficiaryId, setBeneficiaryId] = useState(0);
  const [isLookingUp, setLookingUp] = useState(false);

  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');

  const [showStatus, setShowStatus] = useState(false);
  const onCloseStatusModal = () => {
    setSuccessMsg('');
    setErrorMsg('');
    setShowStatus(false);
    setSubmitStatus(AxiosStatus.DEFAULT);
  };

  const [validatedAccountNo, setValidatedAccountNo] = useState(false);
  const changeValidatedAccount = (value: boolean) => setValidatedAccountNo(value);

  const downMdScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

  const mobileFillingInfo = downMdScreen && state === States.fillingInfo;

  const initialValues: IFormValues = useMemo(() => {
    if (initCategory) {
      const initialData = {
        category: initCategory,
      };
      return initialData;
    }

    return {};
  }, []);

  const validate = useCallback((values: IFormValues) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { category, ...data } = values;
    if (values.category === 'otherBankTransfer' && values.instantType === 'NON-INSTANT') {
      return validationCallbackOld<typeof data>(schema.otherBankTransferNonInstant)(data);
    }
    return validationCallbackOld<typeof data>(schema[category || 'transfer'])(data);
  }, []);

  const getPostBody = (values: IFormValues) => {
    if (values.category === 'FABTransfer') {
      return {
        transferType: 'LOCAL',
        bankCode: 300316,
      };
    }
    if (values.category === 'otherBankTransfer' && values.instantType === 'INSTANT') {
      const selectedBank = additionalData.instantBanks.find((bank) => bank.code === values.bankCode);
      return {
        transferType: 'INSTANT_INTERBANK',
        bankCode: selectedBank?.code,
        bankName: selectedBank?.name,
        beneficiaryId,
      };
    }
    if (values.category === 'otherBankTransfer' && values.instantType === 'NON-INSTANT') {
      const selectedBank = additionalData.nonInstantBanks.find((bank: IBank) => bank.BankSortCode === values.bankCode);
      return {
        transferType: 'NON_INSTANT_INTERBANK',
        bankCode: selectedBank?.BankSortCode,
        bankName: selectedBank?.BankName,
        beneficiaryId,
      };
    }
    if (values.category === 'mobilemoney') {
      const selectedNetwork = additionalData.networks.find((network) => network.code === values.networkCode);
      return {
        network: selectedNetwork?.name,
        networkGipCode: selectedNetwork?.gipCode,
      };
    }
    return {};
  };

  const onAddBeneficiary = (values: IFormValues, form: AddBenFormType) => {
    setSubmitStatus(AxiosStatus.PENDING);
    const requestBody = {
      ...values,
      currency,
      ...getPostBody(values),
    };
    delete requestBody.category;
    delete requestBody.instantType;
    api
      .post(`/beneficiary/${String(beneficiaryTypes[values.category || 0])}`, requestBody)
      .then(() => {
        setSuccessMsg('Beneficiary has been added!');
        setSubmitStatus(AxiosStatus.SUCCESS);
        form.change('beneficiaryWalletNumber', '');
        form.change('accountNumber', '');
        form.restart();
        setNameHint('');
      })
      .catch((error: IApiResponse) => {
        console.error(error);
        setErrorMsg(error.description || 'Beneficiary has not been added!');
        setSubmitStatus(AxiosStatus.FAILURE);
      })
      .finally(() => {
        setShowStatus(true);
      });
  };

  const onSubmit = (values: IFormValues, form: AddBenFormType) => {
    onAddBeneficiary(values, form);
  };

  return (
    <DashboardCard>
      {!downMdScreen && (
        <NavigateBack to="beneficiaries" onClick={onClose} className={styles.mb20}>
          Back
        </NavigateBack>
      )}
      <NavigationTitle
        to="beneficiaries"
        layoutType={LayoutType.dashboard}
        onClick={mobileFillingInfo ? openCategorySelector : onClose}
      >
        Add Beneficiary
      </NavigationTitle>
      <Form
        onSubmit={onSubmit}
        validate={validate}
        initialValues={initialValues}
        mutators={{ changeFieldValue, setFieldTouched, ...arrayMutators }}
        render={({ handleSubmit, values, form, hasValidationErrors }) => (
          <form onSubmit={handleSubmit}>
            {/* <pre>Is Validate Account{JSON.stringify(validatedAccountNo)}</pre> */}
            {/* <pre>{JSON.stringify(values, null, 2)}</pre> */}
            {/* <pre>{JSON.stringify(errors, null, 2)}</pre> */}
            <FieldsLeveler label="Beneficiary category" dividerBottom={!downMdScreen}>
              <Select name="category" label="Beneficiary category" disabled={submitting}>
                {Object.entries(categories).map(([value, label]) => (
                  <SelectItem
                    key={value}
                    value={value}
                    onClick={() => {
                      form.restart();
                    }}
                  >
                    {label}
                  </SelectItem>
                ))}
              </Select>
            </FieldsLeveler>
            <FieldsLeveler label="Beneficiary details">
              {values.category === 'FABTransfer' && (
                <AddBeneficiaryLocal
                  submitting={submitting}
                  setValidatedAccountNo={changeValidatedAccount}
                  nameHint={nameHint}
                  currency={currency}
                  setNameHint={setNameHint}
                  setCurrency={setCurrency}
                  isLookingUp={isLookingUp}
                  setLookingUp={setLookingUp}
                />
              )}
              {values.category === 'otherBankTransfer' && (
                <AddBeneficiaryOtherBank
                  values={values}
                  form={form}
                  instantBanks={additionalData.instantBanks}
                  nonInstantBanks={additionalData.nonInstantBanks}
                  setValidatedAccountNo={changeValidatedAccount}
                  submitting={submitting}
                  nameHint={nameHint}
                  setNameHint={setNameHint}
                  setBeneficiaryId={setBeneficiaryId}
                  isLookingUp={isLookingUp}
                  setLookingUp={setLookingUp}
                />
              )}
              {values.category === 'mobilemoney' && (
                <AddBeneficiaryMobileMoney
                  values={values}
                  form={form}
                  networks={additionalData.networks}
                  submitting={submitting}
                  setValidatedAccountNo={changeValidatedAccount}
                  nameHint={nameHint}
                  setNameHint={setNameHint}
                  isLookingUp={isLookingUp}
                  setLookingUp={setLookingUp}
                />
              )}
              {values.category === 'prepaidcard' && (
                <AddBeneficiaryPrepaidCard
                  form={form}
                  submitting={submitting}
                  setValidatedAccountNo={changeValidatedAccount}
                />
              )}
              {!values.category && (
                <div className={cc(styles.hint, styles.fontSize14)}>
                  To add beneficiary details select beneficiary category first.
                </div>
              )}
            </FieldsLeveler>
            <SubmitLeveler>
              {values.category && (
                <Button type="submit" fullWidth disabled={submitting || hasValidationErrors || !validatedAccountNo}>
                  {submitting ? <CircularProgress size={20} color="inherit" /> : 'Add Beneficiary'}
                </Button>
              )}
            </SubmitLeveler>
            <StatusModal
              open={showStatus}
              status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
              onRetry={onCloseStatusModal}
              onClose={onCloseStatusModal}
              onRetryText="Continue"
            >
              {submitStatus === AxiosStatus.SUCCESS ? successMsg : errorMsg}
            </StatusModal>
          </form>
        )}
      />
    </DashboardCard>
  );
};

export default AddBeneficiaryForm;
